import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Scale, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PageLoading, PageHeader, fmtDate } from "@/components/shared";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ImmigrationRules() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ key: "", value: "", unit: "", source: "", valid_from: "" });

  const load = () => api.get("/admin/immigration-rules").then((r) => setRows(r.data.rules)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    let value = f.value;
    if (!isNaN(Number(value)) && value !== "") value = Number(value);
    else if (typeof value === "string" && value.trim().startsWith("[")) { try { value = JSON.parse(value); } catch {} }
    try { await api.post("/admin/immigration-rules", { ...f, value }); toast.success(t("im.ruleSaved")); setOpen(false); setF({ key: "", value: "", unit: "", source: "", valid_from: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const editRule = (r) => { setF({ key: r.key, value: Array.isArray(r.value) ? JSON.stringify(r.value) : String(r.value ?? ""), unit: r.unit || "", source: r.source || "", valid_from: r.valid_from || "" }); setOpen(true); };

  return (
    <div data-testid="admin-immigration-rules">
      <PageHeader title={t("im.rulesTitle")} subtitle={t("im.rulesSubtitle")} action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="rule-add-btn"><Plus className="mr-1 h-4 w-4" /> {t("im.newRule")}</Button></DialogTrigger>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader><DialogTitle>{t("im.newRule")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>{t("im.ruleKey")}</Label><Input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} placeholder="BLUE_CARD_SALARY_THRESHOLD_GENERAL" data-testid="rule-key" /></div>
              <div className="space-y-1.5"><Label>{t("im.ruleValue")}</Label><Input value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="rule-value" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("im.ruleUnit")}</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("im.ruleValidFrom")}</Label><Input type="date" value={f.valid_from} onChange={(e) => setF({ ...f, valid_from: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>{t("im.ruleSource")}</Label><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="rule-save">{t("f.save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />

      {rows === null ? <PageLoading /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="rules-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">{t("im.ruleKey")}</th><th className="px-4 py-3">{t("im.ruleValue")}</th><th className="px-4 py-3">{t("im.ruleSource")}</th><th className="px-4 py-3">{t("im.ruleValidFrom")}</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50" data-testid={`rule-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{r.key}</td>
                  <td className="px-4 py-3 text-slate-600">{Array.isArray(r.value) ? r.value.join(", ") : String(r.value)} {r.unit}</td>
                  <td className="px-4 py-3 text-slate-500">{r.source || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{r.valid_from || "—"}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${r.status === "ACTIVE" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-right">{r.status === "ACTIVE" && <button onClick={() => editRule(r)} className="text-xs font-semibold text-cyan-dark hover:underline" data-testid={`rule-edit-${r.id}`}>{t("im.newVersion")}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
